"use client";

import React, { useState } from "react";
import { Plus } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: number;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: 1,
  question: "Where can I buy mechanical bull parts?",
  answer: "Mechanical Bull Shop lists available mechanical bull parts and accessories, including controllers, inflatables, blowers and other replacement components when available. Check the individual listing for current price, availability and compatibility information."
}, {
  id: 2,
  question: "How do I know if a mechanical bull part will fit my machine?",
  answer: "Start with the model, serial number, existing part number and clear photos of the component you are replacing. Compatibility should be confirmed for the specific machine rather than assumed from appearance alone."
}, {
  id: 3,
  question: "Can I replace a mechanical bull controller?",
  answer: "A controller can sometimes be replaced, but the replacement needs to match the system it will operate. Compare the existing controller, connections and documented compatibility before ordering another unit."
}, {
  id: 4,
  question: "Do you sell mechanical bull control boxes?",
  answer: "Available control boxes and related controller components appear in the Parts catalog when in stock. Review the product page and confirm compatibility before making an enquiry."
}, {
  id: 5,
  question: "Can I replace the inflatable around a mechanical bull?",
  answer: "Yes, inflatable safety arenas can be replaced when a suitable replacement is available. Compare dimensions, shape, bull-base area, attachment points and blower requirements before choosing one."
}, {
  id: 6,
  question: "Can I use any blower with a mechanical bull inflatable?",
  answer: "No. Blower requirements can vary between inflatables. Check the specification for the exact inflatable rather than assuming every blower is interchangeable."
}, {
  id: 7,
  question: "Can you help identify a part if I do not know its name?",
  answer: "Send the machine model if known, the existing part number if visible, and clear photos of the component, labels and connections. That information can make identification much easier."
}, {
  id: 8,
  question: "Do older mechanical bulls still have replacement parts available?",
  answer: "It depends on the model and component. Some older systems may still use parts that are available, while others may require more careful identification. Check availability before buying an older machine or replacing a major component."
}];
export default function PartsFaq() {
  const [openId, setOpenId] = useState<number | null>(1);
  const shouldReduceMotion = useReducedMotion();
  const toggleItem = (id: number) => {
    setOpenId(prev => prev === id ? null : id);
  };
  return <section id="mechanicalbullpartsfaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-0.5 w-6 bg-primary" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Technical Specifications & Support
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
            Mechanical Bull Parts FAQ
          </h2>

          <div className="mt-4 pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-sm md:text-base text-muted-foreground font-sans max-w-xl leading-relaxed">
              Clear specifications, compatibility guidance, and replacement protocols for commercial operators.
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <Button asChild variant="outline" size="sm" className="rounded-none border-border font-mono text-xs uppercase">
                <Link to="/MechanicalBullPartsAndAccessoriesPage">View Catalog</Link>
              </Button>
              <Button asChild variant="default" size="sm" className="rounded-none font-mono text-xs uppercase">
                <Link to="/ContactPage">Request Identification</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Industrial Accordion Stack */}
        <div className="border-t border-border" role="region" aria-label="Frequently Asked Questions">
          {FAQ_ITEMS.map((item, i) => {
          const isOpen = openId === item.id;
          const contentId = `faq-content-${item.id}`;
          const buttonId = `faq-trigger-${item.id}`;
          return <div key={item.id} data-index={i} className="border-b border-border transition-colors hover:bg-muted/40">
                <Button id={buttonId} variant="ghost" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} aria-controls={contentId} className="w-full flex items-center justify-between py-5 px-3 md:px-4 text-left rounded-none hover:bg-transparent justify-start gap-4 h-auto whitespace-normal">
                  <div className="flex items-start gap-4 flex-1">
                    <span className="font-mono text-xs text-primary font-bold pt-1 shrink-0">
                      0{item.id}
                    </span>
                    <h3 className="font-serif text-lg md:text-xl font-bold uppercase tracking-wide text-foreground leading-snug">
                      {item.question}
                    </h3>
                  </div>

                  <div className="ml-4 shrink-0 flex items-center justify-center size-8 border border-border bg-card text-foreground">
                    <Plus className={`size-4 transition-transform duration-200 ease-out text-foreground ${isOpen ? "rotate-45" : "rotate-0"}`} aria-hidden="true" />
                  </div>
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div id={contentId} role="region" aria-labelledby={buttonId} initial={shouldReduceMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} animate={shouldReduceMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 1,
                height: "auto"
              }} exit={shouldReduceMotion ? {
                opacity: 0,
                height: 0
              } : {
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2,
                ease: "easeOut"
              }} className="overflow-hidden">
                      <div className="pl-11 md:pl-12 pr-4 md:pr-12 pb-6 pt-1">
                        <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Bottom Industrial Note */}
        <div className="mt-12 p-6 bg-card border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
              Notice for Operators
            </span>
            <p className="font-sans text-xs md:text-sm text-muted-foreground">
              Equipment sales only. Always verify part numbers, electrical tolerances, and dimensions prior to installation.
            </p>
          </div>
          <Button asChild variant="secondary" size="sm" className="rounded-none font-mono text-xs uppercase shrink-0">
            <Link to="/ContactPage">Contact Technical Desk</Link>
          </Button>
        </div>
      </div>
    </section>;
}